'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Body, Caption } from '@depop/web-thrift-ui/Typography';
import { Filter2RowIcon } from '@depop/web-ui-kit/Icons/Filter2RowIcon';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { ActiveFilters } from '../ActiveFilters';

import { BrandFilter } from './BrandFilter';
import { CategoryFilter, SubcategoryFilter } from './CategoryFilters';
import { ColourFilter } from './ColourFilter';
import { ConditionFilter } from './ConditionFilter';
import { MobileFilterFooter } from './MobileFilterFooter';
import { MobileFilterHeader } from './MobileFilterHeader';
import { OnSaleFilter } from './OnSaleFilter';
import { PriceFilter } from './PriceFilter';
import { SizeFilter } from './SizeFilter';
import { SortByFilter } from './SortByFilter';
import styles from './baseFilterStyles.module.css';

import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { FilterLocation } from '@/modules/filters/types';
import { useFiltersTracking } from '@/modules/filters/useFiltersTracking';
import { SORT_BY_OPTIONS } from '@/modules/search/constants';
import { getActiveFiltersCount } from '@/modules/search/helpers';
import { SortByValues } from '@/modules/search/types';
import { useSearchDrawer } from '@/modules/search/useSearchDrawer';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

const STICKY_FILTERS_TOP_PAGE_BUFFER_HEIGHT = 400;

type Props = {
  showActiveFilters?: boolean;
};

export function Filters({ showActiveFilters = true }: Props) {
  const { searchFilters, setSearchFilters } = useSearchFilters();
  const t = useTranslations('search');

  const [authenticated, user] = useCurrentUser();
  const sortByOptions = authenticated
    ? SORT_BY_OPTIONS
    : SORT_BY_OPTIONS.filter(
        (option) => option.id !== SortByValues.NewlyListed
      );
  const [showStickyFilters, setShowStickyFilters] = useState(false);
  const scrollRef = useRef({
    previousScrollY: 0,
    ticking: false,
    showing: false,
  });
  const { isOpen: isFilterDrawerOpen, setIsOpen: setIsFilterDrawerOpen } =
    useSearchDrawer();

  const { sendActivityTrackerEvent } = useActivityTracker({ userId: user?.id });
  useFiltersTracking();

  function closeMobileFilterDrawer() {
    setIsFilterDrawerOpen(false);
  }

  function handleFilterOpenClick(type: FilterLocation) {
    sendActivityTrackerEvent(ActivityTrackerEventType.FILTER_OPENED_ACTION, {
      filterType: type,
    });
    setIsFilterDrawerOpen((prev) => !prev);
  }

  const handleScroll = useCallback(() => {
    const isScrollingUp = window.scrollY < scrollRef.current.previousScrollY;
    const isFarDownPage =
      window.scrollY > STICKY_FILTERS_TOP_PAGE_BUFFER_HEIGHT;
    const wasShowingLastTick = scrollRef.current.showing;
    const shouldShowStickyFilters =
      isScrollingUp && (isFarDownPage || wasShowingLastTick);
    setShowStickyFilters(shouldShowStickyFilters);
    scrollRef.current.previousScrollY = window.scrollY;
    scrollRef.current.showing = shouldShowStickyFilters;
    scrollRef.current.ticking = false;
  }, [setShowStickyFilters]);

  const onScroll = useCallback(() => {
    if (!scrollRef.current.ticking) {
      window.requestAnimationFrame(handleScroll);
      scrollRef.current.ticking = true;
    }
  }, [handleScroll]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  const activeFiltersCount = getActiveFiltersCount(searchFilters);

  return (
    <div
      className={clsx(styles.filterWrapper, {
        [styles.sticky]: showStickyFilters,
        [styles.filtersOpen]: isFilterDrawerOpen,
      })}
    >
      <div className={styles.filterAndSortContainer}>
        <button
          onClick={() => handleFilterOpenClick(FilterLocation.Mobile)}
          className={styles.mobileFilterButton}
        >
          <Filter2RowIcon size={22} />
          <Body as="span" variant="md-bold">
            {t('AllFilters')}
          </Body>
          {activeFiltersCount > 0 && (
            <Caption
              as="span"
              colour="base"
              variant="lg-regular"
              className={styles.filterCount}
            >
              {activeFiltersCount}
            </Caption>
          )}
        </button>

        <div
          className={clsx(
            styles.filtersContainer,
            !isFilterDrawerOpen && styles.hidden
          )}
        >
          <MobileFilterHeader
            closeMobileFilterDrawer={closeMobileFilterDrawer}
            title={t('AllFilters')}
          />

          <div className={styles.dropdownsContainer}>
            <CategoryFilter closeMobileFilterDrawer={closeMobileFilterDrawer} />
            <SubcategoryFilter
              closeMobileFilterDrawer={closeMobileFilterDrawer}
            />
            <BrandFilter
              showSearchBar
              closeMobileFilterDrawer={closeMobileFilterDrawer}
            />
            <PriceFilter closeMobileFilterDrawer={closeMobileFilterDrawer} />
            <SizeFilter closeMobileFilterDrawer={closeMobileFilterDrawer} />
            <ColourFilter closeMobileFilterDrawer={closeMobileFilterDrawer} />
            <ConditionFilter
              closeMobileFilterDrawer={closeMobileFilterDrawer}
            />
            <OnSaleFilter />
          </div>

          {showActiveFilters && <ActiveFilters />}

          {/* empty div to fill the space on mobile in lieu of dropdown content */}
          <div className={styles.flexGrow} />

          <MobileFilterFooter
            handleClear={setSearchFilters.clearAll}
            handleSubmit={closeMobileFilterDrawer}
          />
        </div>

        <div className={styles.divider} />
        <SortByFilter options={sortByOptions} />
      </div>
    </div>
  );
}
